/**
 * components/SaveRecipeModal.js
 * Name-first "Simpan sebagai resep" modal. Collects a name (wajib) + optional note for a snapshot
 * { type, inputs, outputs } and shows a read-only summary so naming has context. Owns its own
 * name/note/error state; calls onSave({ name, note }) — the parent builds the recipe from the snapshot.
 * Esc / click-outside / Batal close without saving; Enter in the name field saves.
 */
import React, { useState } from "react";
import { fmtQty } from "../utils/idGenerators";
import { useModalEsc, backdropClose } from "../utils/modalStack";

const TYPE_LABEL = { ASSEMBLE: "Rakit (Assemble)", TEARDOWN: "Urai (Teardown)" };

export default function SaveRecipeModal({ snapshot, byCode, onSave, onClose, intro, recipes = [] }) {
  const [name, setName] = useState(snapshot.name || "");
  const [note, setNote] = useState(snapshot.note || "");
  const [err, setErr] = useState("");

  const trimmedName = name.trim();
  // Normalize for duplicate comparison: lowercase, trim, and collapse internal whitespace runs
  // to a single space — so "pg x sg" and "pg x  sg" are treated as the same name.
  const normName = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
  // Stored-name normalizer: trim + collapse internal whitespace, but KEEP the user's casing.
  const tidyName = (s) => String(s || "").trim().replace(/\s+/g, " ");
  const isDuplicate =
    trimmedName.length > 0 &&
    recipes.some((r) => normName(r.name) === normName(name));

  useModalEsc(onClose);

  const save = () => {
    const n = tidyName(name);
    if (!n) { setErr("Nama resep wajib diisi."); return; }
    if (isDuplicate) { setErr("Nama resep sudah dipakai — pilih nama lain."); return; }
    onSave({ name: n, note: note.trim() });
  };

  const renderLines = (lines) => (
    <ul className="recipe-lines">
      {lines.map((l, i) => {
        const it = byCode.get(l.internalCode);
        return (
          <li key={i}>
            <span className="mono">{l.internalCode}</span> ·{" "}
            {it ? it.name : <span style={{ color: "var(--red)" }}>tidak tersedia</span>}
            {" "}× <b>{fmtQty(l.qty)}</b>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Simpan sebagai resep</h2>
        {intro && <div className="alert alert--info" style={{ marginBottom: "var(--gap-lg)" }}>{intro}</div>}
        <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          {TYPE_LABEL[snapshot.type] || snapshot.type}
        </div>

        {err && <div className="alert alert--error">{err}</div>}

        <div className="form-row">
          <label className="form-label" htmlFor="save-recipe-nama">Nama resep (wajib)</label>
          <input id="save-recipe-nama" className="form-input" value={name} autoFocus
            placeholder="cth. Gardan 8x39 versi LS"
            onChange={(e) => { setName(e.target.value); if (err) setErr(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          {isDuplicate && <div className="form-hint" style={{ color: "var(--red)" }}>Nama resep sudah dipakai — pilih nama lain.</div>}
          {/* Disabled-control sweep (manual-testing report) — shared convention. trimmedName==""
              implies !isDuplicate (isDuplicate's own check requires a non-empty trimmedName), so
              these two hints never show at once. */}
          {!trimmedName && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
        </div>
        <div className="form-row">
          <label className="form-label" htmlFor="save-recipe-catatan">Catatan (opsional)</label>
          <input id="save-recipe-catatan" className="form-input" value={note} placeholder="Catatan resep"
            onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="card" style={{ marginBottom: 0, background: "#f8fafc" }}>
          <div className="form-label">Input</div>
          {snapshot.inputs.length ? renderLines(snapshot.inputs) : <div className="form-hint">—</div>}
          <div className="form-label" style={{ marginTop: "var(--gap-md)" }}>Output</div>
          {snapshot.outputs.length ? renderLines(snapshot.outputs) : <div className="form-hint">—</div>}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="button" className="btn btn-primary" disabled={!trimmedName || isDuplicate} onClick={save}>Simpan resep</button>
        </div>
      </div>
    </div>
  );
}
